import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api.get('/auth/me')
        .then(response => {
          setUser(response.data.user);
        })
        .catch(error => {
          console.error('Auth check failed:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const { token, user: u } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(u));
      setUser(u);

      return { success: true };
    } catch (error) {
      const data = error.response?.data || {};
      return {
        success: false,
        message: data.message || 'Login failed',
        emailNotVerified: data.emailNotVerified || false,
        email: data.email || '',
      };
    }
  };

  const register = async (userData) => {
    try {
      const payload = {
        ...userData,
        email: String(userData.email || '').trim().toLowerCase(),
      };
      const response = await api.post('/auth/register', payload);
      const data = response.data;

      if (data.requiresVerification) {
        return { success: true, requiresVerification: true, email: data.email };
      }

      if (data.token && data.user) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        setUser(data.user);
      }

      return { success: true };
    } catch (error) {
      const d = error.response?.data;
      const msg =
        d?.message ||
        (Array.isArray(d?.errors) && d.errors[0]?.msg) ||
        'Registration failed';
      return { success: false, message: msg };
    }
  };

  const verifyRegistrationOtp = async (email, otp) => {
    try {
      const response = await api.post('/auth/verify-email', {
        email: String(email).trim().toLowerCase(),
        otp: String(otp).replace(/\D/g, ''),
      });
      const { token, user: u } = response.data;
      if (token && u) {
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(u));
        setUser(u);
      }
      return { success: true };
    } catch (error) {
      const d = error.response?.data;
      return {
        success: false,
        message: d?.message || 'Verification failed',
        expired: Boolean(d?.expired),
        tooManyAttempts: Boolean(d?.tooManyAttempts),
      };
    }
  };

  const googleLogin = async (credential) => {
    try {
      const response = await api.post('/auth/google', { credential });
      const { token, user: u } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(u));
      setUser(u);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Google login failed',
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const setUserDirect = (u) => setUser(u);

  const refreshUser = async () => {
    try {
      const response = await api.get('/auth/me');
      const u = response.data.user;
      setUser(u);
      localStorage.setItem('user', JSON.stringify(u));
      return { success: true, user: u };
    } catch (error) {
      console.error('refreshUser failed:', error);
      return { success: false };
    }
  };

  const value = {
    user,
    login,
    register,
    verifyRegistrationOtp,
    googleLogin,
    logout,
    loading,
    setUserDirect,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
